export type StructureSlot = {
  id: number;
  name: string;
};

export type StructureShelf = {
  id: number;
  name: string;
  slots: StructureSlot[];
};

export type StructureRack = {
  id: number;
  name: string;
  shelves: StructureShelf[];
  slots?: StructureSlot[];
};

export type StructureAisle = {
  id: number;
  name: string;
  racks: StructureRack[];
};

export type StructureZone = {
  id: number;
  name: string;
  aisles: StructureAisle[];
  racks?: StructureRack[];
};

export type WarehouseStructureTree = {
  zones: StructureZone[];
  aisles: StructureAisle[];
  racks: StructureRack[];
};

export type RootStructureRow = {
  type: 'zone' | 'aisle' | 'rack';
  rootItem: StructureZone | StructureAisle | StructureRack;
};

export type RowSelectionState = {
  zoneId?: number | null;
  aisleId?: number | null;
  rackId?: number | null;
  shelfId?: number | null;
};

export function getAislesFromStructure(
  structure: WarehouseStructureTree,
  selectedZoneId: number | null,
): StructureAisle[] {
  if (!selectedZoneId) {
    return structure.aisles;
  }
  const zone = structure.zones.find((z) => z.id === selectedZoneId);
  return zone?.aisles || [];
}

export function getRacksFromStructure(
  structure: WarehouseStructureTree,
  selectedZoneId: number | null,
  selectedAisleId: number | null,
): StructureRack[] {
  const aisles = getAislesFromStructure(structure, selectedZoneId);
  if (selectedAisleId) {
    const aisle = aisles.find((a) => a.id === selectedAisleId);
    return aisle?.racks || [];
  }
  if (selectedZoneId) {
    const zone = structure.zones.find((z) => z.id === selectedZoneId);
    return zone?.racks || [];
  }
  return structure.racks;
}

export function findAllRacksInStructure(
  structure: WarehouseStructureTree,
): StructureRack[] {
  const racks: StructureRack[] = [...structure.racks];
  structure.zones.forEach((z) => {
    if (z.racks) {
      racks.push(...z.racks);
    }
    z.aisles.forEach((a) => racks.push(...a.racks));
  });
  structure.aisles.forEach((a) => racks.push(...a.racks));
  return racks;
}

export function getShelvesFromStructure(
  structure: WarehouseStructureTree,
  selectedRackId: number | null,
): StructureShelf[] {
  const rack = findAllRacksInStructure(structure).find(
    (r) => r.id === selectedRackId,
  );
  return rack?.shelves || [];
}

export function getSlotsFromStructure(
  structure: WarehouseStructureTree,
  selectedRackId: number | null,
  selectedShelfId: number | null,
): StructureSlot[] {
  const shelf = getShelvesFromStructure(structure, selectedRackId).find(
    (s) => s.id === selectedShelfId,
  );
  return shelf?.slots || [];
}

export function getRackDirectSlotsFromStructure(
  structure: WarehouseStructureTree,
  selectedRackId: number | null,
): StructureSlot[] {
  const rack = findAllRacksInStructure(structure).find(
    (r) => r.id === selectedRackId,
  );
  return rack?.slots || [];
}

export function hasStructureChildren(
  item: unknown,
  itemType: 'zone' | 'aisle' | 'rack' | 'shelf' | 'bin',
): boolean {
  if (itemType === 'zone') {
    const zone = item as StructureZone;
    return !!(zone.aisles?.length || zone.racks?.length);
  }
  if (itemType === 'aisle') {
    const aisle = item as StructureAisle;
    return !!aisle.racks?.length;
  }
  if (itemType === 'rack') {
    const rack = item as StructureRack;
    return !!(rack.shelves?.length || rack.slots?.length);
  }
  if (itemType === 'shelf') {
    const shelf = item as StructureShelf;
    return !!shelf.slots?.length;
  }
  return false;
}

export function getShelvesForStructureRow(
  row: RootStructureRow,
  rowSelections: RowSelectionState,
): StructureShelf[] {
  const rackId = rowSelections.rackId;
  if (!rackId) return [];

  if (row.type === 'zone') {
    const zone = row.rootItem as StructureZone;
    const rack = rowSelections.aisleId
      ? zone.aisles
          .find((a) => a.id === rowSelections.aisleId)
          ?.racks.find((r) => r.id === rackId)
      : zone.racks?.find((r) => r.id === rackId);
    return rack?.shelves || [];
  }
  if (row.type === 'aisle') {
    const aisle = row.rootItem as StructureAisle;
    return aisle.racks.find((r) => r.id === rackId)?.shelves || [];
  }
  const rack = row.rootItem as StructureRack;
  return rack.shelves || [];
}

export function getSlotsForStructureRow(
  row: RootStructureRow,
  rowSelections: RowSelectionState,
): StructureSlot[] {
  const shelfId = rowSelections.shelfId;
  if (!shelfId) return [];
  const shelf = getShelvesForStructureRow(row, rowSelections).find(
    (s) => s.id === shelfId,
  );
  return shelf?.slots || [];
}

export function getRackDirectSlotsForStructureRow(
  row: RootStructureRow,
  rowSelections: RowSelectionState,
): StructureSlot[] {
  const rackId = rowSelections.rackId;
  if (!rackId) return [];

  if (row.type === 'zone') {
    const zone = row.rootItem as StructureZone;
    const rack = rowSelections.aisleId
      ? zone.aisles
          .find((a) => a.id === rowSelections.aisleId)
          ?.racks.find((r) => r.id === rackId)
      : zone.racks?.find((r) => r.id === rackId);
    return rack?.slots || [];
  }
  if (row.type === 'aisle') {
    const aisle = row.rootItem as StructureAisle;
    return aisle.racks.find((r) => r.id === rackId)?.slots || [];
  }
  const rack = row.rootItem as StructureRack;
  return rack.id === rackId ? rack.slots || [] : [];
}
